import React from 'react';
import { motion } from 'framer-motion';
import { FiPhone, FiMail, FiMapPin, FiInstagram, FiFacebook, FiMessageCircle } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const Footer = () => {
  const quickLinks = [
    { name: 'Sanctuary', href: '#sanctuary' },
    { name: 'Experiences', href: '#experiences' },
    { name: 'Gallery', href: '#gallery' },
    { name: 'Logistics', href: '#logistics' },
    { name: 'Dining', href: '#dining' },
    { name: 'Book Now', href: '#booking' }
  ];

  const experiences = [
    'Birdwatching Tours',
    'Waterfall Trekking',
    'River Bathing',
    'Night Walks',
    'Photography Tours',
    'Cultural Experiences'
  ];

  const contactInfo = [
    {
      icon: FiPhone,
      label: 'Call Us',
      value: '+94 XX XXX XXXX',
      action: 'tel:+94XXXXXXXXX'
    },
    {
      icon: FiMessageCircle,
      label: 'WhatsApp',
      value: '+94 XX XXX XXXX',
      action: 'https://wa.me/94XXXXXXXXX'
    },
    {
      icon: FiMail,
      label: 'Email',
      value: 'info@sinharajaserenity.com',
      action: 'mailto:info@sinharajaserenity.com'
    },
    {
      icon: FiMapPin,
      label: 'Address',
      value: 'Mederipitiya, Deniyaya, Southern Province, Sri Lanka',
      action: '#'
    }
  ];

  const socialLinks = [
    { icon: FiFacebook, href: '#', label: 'Facebook' },
    { icon: FiInstagram, href: '#', label: 'Instagram' },
    { icon: FiMessageCircle, href: '#', label: 'WhatsApp' }
  ];

  return (
    <footer className="relative py-16 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 glass-morphism" />
      
      <div className="container mx-auto px-6 relative z-10">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
          {/* Brand & Description */}
          <motion.div
            className="lg:col-span-1"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-12 h-12 rounded-full glass-card flex items-center justify-center">
                <span className="text-2xl">🌿</span>
              </div>
              <div>
                <h3 className="text-xl font-bold text-white font-['Playfair_Display']">
                  Sinharaja Serenity
                </h3>
                <p className="text-sm text-gray-300">Retreat</p>
              </div>
            </div>
            <p className="text-gray-300 text-sm leading-relaxed mb-4">
              Your authentic gateway to the Sinharaja Rainforest. Experience pristine nature, 
              endemic wildlife, and unparalleled serenity in Mederipitiya.
            </p>
            
            {/* Social Links */}
            <div className="flex space-x-3">
              {socialLinks.map((social, index) => (
                <motion.a
                  key={social.label}
                  href={social.href}
                  className="w-10 h-10 glass-card rounded-full flex items-center justify-center hover-glow"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.1 * index }}
                  viewport={{ once: true }}
                >
                  <SafeIcon icon={social.icon} className="w-5 h-5 text-gray-300" />
                </motion.a>
              ))}
            </div>
          </motion.div>

          {/* Quick Links */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            viewport={{ once: true }}
          >
            <h4 className="text-lg font-semibold text-white mb-4 font-['Playfair_Display']">
              Quick Links
            </h4>
            <ul className="space-y-2">
              {quickLinks.map((link, index) => (
                <motion.li
                  key={link.name}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * index }}
                  viewport={{ once: true }}
                >
                  <a
                    href={link.href}
                    className="text-gray-300 hover:text-white transition-colors text-sm"
                  >
                    {link.name}
                  </a>
                </motion.li>
              ))}
            </ul>
          </motion.div>

          {/* Experiences */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <h4 className="text-lg font-semibold text-white mb-4 font-['Playfair_Display']">
              Experiences
            </h4>
            <ul className="space-y-2">
              {experiences.map((experience, index) => (
                <motion.li
                  key={experience}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * index }}
                  viewport={{ once: true }}
                >
                  <span className="text-gray-300 text-sm">{experience}</span>
                </motion.li>
              ))}
            </ul>
          </motion.div>

          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            viewport={{ once: true }}
          >
            <h4 className="text-lg font-semibold text-white mb-4 font-['Playfair_Display']">
              Contact Us
            </h4>
            <div className="space-y-3">
              {contactInfo.map((contact, index) => (
                <motion.a
                  key={contact.label}
                  href={contact.action}
                  className="flex items-start space-x-3 text-gray-300 hover:text-white transition-colors group"
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * index }}
                  viewport={{ once: true }}
                >
                  <div className="w-5 h-5 mt-0.5 flex-shrink-0">
                    <SafeIcon icon={contact.icon} className="w-5 h-5 text-blue-400 group-hover:text-blue-300" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 uppercase tracking-wider">
                      {contact.label}
                    </p>
                    <p className="text-sm">{contact.value}</p>
                  </div>
                </motion.a>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Newsletter Signup */}
        <motion.div
          className="glass-card p-6 mb-8"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          viewport={{ once: true }}
        >
          <div className="text-center">
            <h4 className="text-lg font-semibold text-white mb-2 font-['Playfair_Display']">
              Stay Connected with Nature
            </h4>
            <p className="text-gray-300 text-sm mb-4">
              Subscribe to receive updates on seasonal wildlife, special offers, and rainforest conservation news
            </p>
            <div className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
              <input
                type="email"
                placeholder="Enter your email address"
                className="flex-1 px-4 py-3 glass-morphism text-white placeholder-gray-400 rounded-lg border-0 focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
              <motion.button
                className="px-6 py-3 bg-gradient-to-r from-green-600 to-blue-600 text-white font-semibold rounded-lg hover-glow"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Subscribe
              </motion.button>
            </div>
          </div>
        </motion.div>

        {/* Bottom Bar */}
        <motion.div
          className="border-t border-white/20 pt-8"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          viewport={{ once: true }}
        >
          <div className="flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0">
            <div className="text-center md:text-left">
              <p className="text-gray-300 text-sm">
                © 2025 Sinharaja Serenity Retreat. All rights reserved.
              </p>
              <p className="text-gray-400 text-xs mt-1">
                Designed and Created by Rajantha
              </p>
            </div>
            
            <div className="flex items-center space-x-6 text-xs text-gray-400">
              <a href="#" className="hover:text-white transition-colors">Privacy Policy</a>
              <a href="#" className="hover:text-white transition-colors">Terms of Service</a>
              <a href="#" className="hover:text-white transition-colors">Sustainability</a>
            </div>
          </div>
        </motion.div>

        {/* Floating WhatsApp Button */}
        <motion.div
          className="fixed bottom-6 right-6 z-50"
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 2, type: "spring", stiffness: 260, damping: 20 }}
        >
          <motion.a
            href="https://wa.me/94XXXXXXXXX"
            target="_blank"
            rel="noopener noreferrer"
            className="w-14 h-14 bg-green-500 rounded-full flex items-center justify-center shadow-lg hover:shadow-xl transition-shadow pulse-glow"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            <SafeIcon icon={FiMessageCircle} className="w-7 h-7 text-white" />
          </motion.a>
        </motion.div>
      </div>
    </footer>
  );
};

export default Footer;